import {
  ACCEPT_HEADER,
  API_KEY_HEADER,
  AUTHORIZATION_HEADER_ID,
  getRequestUrlFromConfig,
  HeaderRequestId,
  LOG_URL,
  SENSITIVE_DATA_BRANDED_TYPE,
} from '@blk/aw-api-clients';
import { EventType, LogAction, Logger, LoggerUtil } from '@blk/aw-logger';
import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { get, omit } from 'lodash';
import { GenStatV2 } from './logger';

export const APP_RELOAD_FLAG_KEY = 'hasAppReloaded';

const OMIT_FROM_ERROR_LOGGING = [
  'request',
  'config.data',
  'config.headers.cookie',
  'config.headers.Cookie',
  'config.headers.' + ACCEPT_HEADER,
  'config.headers.' + API_KEY_HEADER,
  'config.headers.' + AUTHORIZATION_HEADER_ID,
];

/** Check if the request URL is of analytics API */
const isStatsUrl = (url: string) =>
  ['/stats/v2', '/statcollector/v1', '/farocollector', LOG_URL].some((pattern) => url.match(pattern));

/** Recursively find field paths containing the sensitive data brand */
const findSensitiveFields = (obj: unknown, maxDepth: number = 10): string[] => {
  const results: string[] = [];
  const seen = new WeakSet<object>();

  const walk = (value: unknown, path: string, depth: number): void => {
    if (depth <= 0 || value === null || value === undefined) {
      return;
    }

    if (typeof value === 'string') {
      if (value.includes(SENSITIVE_DATA_BRANDED_TYPE)) {
        results.push(path || '(root)');
      }
      return;
    }

    if (typeof value === 'object') {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);

      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          walk(item, `${path}[${index}]`, depth - 1);
        });
      } else {
        for (const [key, child] of Object.entries(value)) {
          walk(child, path ? `${path}.${key}` : key, depth - 1);
        }
      }
    }
  };

  walk(obj, '', maxDepth);
  return results;
};

/**
 * Intercepts requests to check if payload contains sensitive data
 * @param config AxiosRequestConfig
 * @returns AxiosRequestConfig
 */
const axiosCheckSensitiveDataInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  if (config.data && config.url && !isStatsUrl(config.url)) {
    const payloadString = typeof config.data === 'string' ? config.data : JSON.stringify(config.data);

    if (LoggerUtil.containsSensitiveData(payloadString)) {
      // Identify specific fields containing sensitive data
      const sensitiveFields = findSensitiveFields(config.data);

      Logger.trackEvent({
        action: LogAction.SENSITIVE_DATA_DETECTED,
        elementInfo: {
          eventType: EventType.EVENT_LOAD,
          metadata: JSON.stringify({
            requestId: config.headers?.[HeaderRequestId.NAME] ?? '',
            requestUrl: getRequestUrlFromConfig(config),
            requestMethod: config.method,
            sensitiveFields,
          }),
        },
      });
    }
  }
  return config;
};

export const getResponseTime = (config: AxiosRequestConfig): number | undefined => {
  const lastRequest = config['axios-retry']?.lastRequestTime;
  return lastRequest ? Date.now() - lastRequest : undefined;
};

/**
 * Get retry counts from the axios config
 * @param config AxiosRequestConfig
 * @returns { retryCount: number, maxRetryCount: number }
 */
export const getRetryCounts = (config: AxiosRequestConfig): { retryCount: number; maxRetryCount: number } => {
  const axiosRetryConfig = config['axios-retry'];
  const retryCount = axiosRetryConfig?.retryCount ?? 0;
  const maxRetryCount = axiosRetryConfig?.retries ?? 2;
  return { retryCount, maxRetryCount };
};

// for cancelled axios requests, the error does not contain a config object, thus we need to check if error.config exists
const getRequestIdFromAxiosError = (error: Partial<AxiosError>): string =>
  String(error?.config?.headers?.[HeaderRequestId.NAME] ?? '');

const isUnknownError = (error: Partial<AxiosError>): boolean => !error?.config;

const generateGenStatPayload = (
  error: Partial<AxiosError>,
  stringifiedError: string
): Pick<GenStatV2, 'attrib1' | 'attrib2' | 'data1'> => {
  let logPayload: Pick<GenStatV2, 'attrib1' | 'attrib2' | 'data1'>;
  if (isUnknownError(error)) {
    logPayload = {
      attrib1: 'Unknown',
      attrib2: stringifiedError,
    };
  } else {
    const responseStatus = get(error, 'response.status');
    logPayload = {
      data1: responseStatus,
      attrib1: error?.config?.url,
      attrib2: stringifiedError,
    };
  }
  return logPayload;
};

const generateErrorContentForConsole = (error: Partial<AxiosError>, stringifiedError: string): string => {
  let errorContent: string;
  if (isUnknownError(error)) {
    errorContent = `Unknown Error Occurred. Error received: ${stringifiedError}`;
  } else {
    let responseData = get(error, 'response.data', 'No data received');
    const responseStatus = get(error, 'response.status');
    if (typeof responseData === 'object') {
      // try to extract some information out of the object
      if (responseData && 'data' in responseData) {
        responseData = responseData.data;
      }
      responseData = JSON.stringify(responseData);
    }
    errorContent =
      `HTTP request to ${error?.config?.url} has failed with HTTP status code ${String(responseStatus)}\n` +
      `Response was: ${String(responseData)}`;
  }
  return errorContent;
};

const printErrorToConsole = (requestId: string, errorContent: string) => {
  const requestIdMsg = 'Request ID: ' + requestId + '\n';
  console.error(
    '%c*********AW HTTP Request failure report*********\n' +
      requestIdMsg +
      errorContent +
      '\n*********End report*********',
    'background: red; color: white;'
  );
};

const axiosLogErrorToGenStatInterceptor = (
  error: AxiosError,
  optionalPayloadConsumer?: (payload: Pick<GenStatV2, 'attrib1' | 'attrib2' | 'data1'>) => void
) => {
  /*
   * Log all HTTP error attempts including retries
   * Don't log HTTP request failures of stats so we don't end up in an infinite loop
   */
  if (error.config?.url && !isStatsUrl(error.config.url)) {
    const { retryCount, maxRetryCount } = getRetryCounts(error.config);

    const sanitizedError = omit(error, OMIT_FROM_ERROR_LOGGING);
    const stringifiedError = JSON.stringify(sanitizedError);
    const requestId: string = getRequestIdFromAxiosError(sanitizedError);
    const errContent: string = generateErrorContentForConsole(sanitizedError, stringifiedError);
    printErrorToConsole(requestId, errContent);
    const configWithCorrelation = error.config as AxiosRequestConfig & { correlationId?: string };
    const attemptNumber = retryCount + 1;
    const isFinal = !!retryCount && retryCount === maxRetryCount;

    if (optionalPayloadConsumer !== undefined) {
      const genStatPayload: Pick<GenStatV2, 'attrib1' | 'attrib2' | 'data1'> = generateGenStatPayload(
        sanitizedError,
        stringifiedError
      );
      optionalPayloadConsumer(genStatPayload);
    }
    Logger.trackEvent({
      action: LogAction.HTTP_ERROR,
      elementInfo: {
        eventType: EventType.EVENT_ERROR,
        metadata: JSON.stringify({
          responseTime: getResponseTime(error.config),
          attemptNumber,
          maxRetries: maxRetryCount,
          isFinal,
          correlationId: configWithCorrelation.correlationId,
        }),
      },
      error: { requestId, errorStackTrace: stringifiedError },
    });
  }

  return Promise.reject(error);
};

const reloadAlert = (reloadNotification?: () => void) => {
  localStorage.setItem(APP_RELOAD_FLAG_KEY, 'true');
  reloadNotification?.();
};

export const handleSessionExpiry = (response: AxiosResponse, reloadNotification?: () => void) => {
  // TODO: Remove this code once we have a better way to handle session expiry via 302 status code
  /*
   * Currently, when a user's session expires and if the app makes any subsequent API calls, the server responds
   * with a 302 redirect to the Okta login urls. Since axios is not able to intercept 302 redirects, the following
   * auth URLs are used to check if the user's session is valid. If the session has expired, the app will be
   * reloaded to redirect user to sign-in page.
   */
  const authUrls = [
    `${window.location.origin}/auth_gw/oauth2/v1/authorize`,
    `${window.location.origin}/auth_gw/login/login.htm`,
  ];

  const responseURL: string | undefined =
    typeof response.request === 'object' && response.request !== null
      ? (response.request as { responseURL?: string }).responseURL
      : undefined;
  if (authUrls.some((url) => responseURL?.includes(url)) && localStorage.getItem(APP_RELOAD_FLAG_KEY) !== 'true') {
    console.warn('Session expiry redirect detected');
    Logger.trackEvent({
      action: LogAction.SESSION_EXPIRY_REDIRECT_DETECTED,
      elementInfo: {
        eventType: EventType.EVENT_ERROR,
        metadata: JSON.stringify({
          requestUrl: response.config?.url,
          responseURL,
        }),
      },
    });
    reloadAlert(reloadNotification);
    /*
     * Reject the promise so callers receive an error rather than the login-page HTML body.
     * Without this, selectors that validate response shape (e.g. /dates) would see an
     * empty/malformed payload and throw a fatal error before the session-expiry UI can render.
     */
    throw new AxiosError('Session expired', 'ERR_SESSION_EXPIRED', response.config, response.request, response);
  }
  return response;
};

export const handleApiFailure = (
  error: AxiosError,
  optionalPayloadConsumer?: (payload: Pick<GenStatV2, 'attrib1' | 'attrib2' | 'data1'>) => void,
  reloadNotification?: () => void
) => {
  if (
    error?.config?.url === '/stats/v2' &&
    error?.response?.status === 401 &&
    localStorage.getItem(APP_RELOAD_FLAG_KEY) !== 'true'
  ) {
    reloadAlert(reloadNotification);
  }
  return axiosLogErrorToGenStatInterceptor(error, optionalPayloadConsumer);
};

const logAPICalls = (res: AxiosRequestConfig, status: number | undefined) => {
  Logger.trackEvent({
    action: LogAction.HTTP_REQUEST,
    elementInfo: {
      eventType: EventType.EVENT_LOAD,
      metadata: JSON.stringify({
        requestId: res.headers?.[HeaderRequestId.NAME] ?? '',
        requestUrl: getRequestUrlFromConfig(res),
        requestMethod: res?.method,
        responseStatus: status,
        responseTime: getResponseTime(res),
      }),
    },
  });
};

/**
 * Intercept fulfilled responses to log when we succeed after retries
 * @param response AxiosResponse
 * @param optionalResponseInterceptor (optional) response interceptor
 * @returns AxiosResponse
 */
export const interceptFulfilledResponse = (
  response: AxiosResponse,
  optionalResponseInterceptor?: (response: AxiosResponse) => AxiosResponse,
  reloadNotification?: () => void
) => {
  handleSessionExpiry(response, reloadNotification);
  const { retryCount, maxRetryCount } = getRetryCounts(response.config);
  if (response.config?.url && !isStatsUrl(response.config.url)) {
    logAPICalls(response.config, response.status);
    // Log when we succeed after retries
    if (retryCount && retryCount <= maxRetryCount) {
      Logger.trackEvent({
        action: LogAction.API_CALL_SUCCEEDED_AFTER_RETRY,
        elementInfo: {
          metadata: JSON.stringify({
            numberOfRetries: retryCount,
            requestUrl: getRequestUrlFromConfig(response.config),
            requestMethod: response.config.method,
            responseStatus: response.status,
            responseTime: getResponseTime(response.config),
          }),
        },
      });
    }
  }
  return optionalResponseInterceptor ? optionalResponseInterceptor(response) : response;
};

const STATUS_CODES_TO_RETRY: number[] = [
  408, // Request Timeout
  502, // Bad Gateway
  503, // Service Unavailable
  504, // Gateway Timeout
];

/**
 * Setup retry for the axios instance
 * @param axiosApiInstance AxiosInstance
 * @param noOfRetries number of retries, default is 2
 */
export const setupRetry = (axiosApiInstance: AxiosInstance, noOfRetries: number = 2) => {
  axiosRetry(axiosApiInstance, {
    retries: noOfRetries,
    retryDelay: (retryNumber: number, error: AxiosError) => axiosRetry.exponentialDelay(retryNumber, error),
    retryCondition: (error: AxiosError) =>
      axiosRetry.isNetworkError(error) || STATUS_CODES_TO_RETRY.includes(error.response?.status ?? 0),
    onRetry: (retryCount: number, error: AxiosError, requestConfig: AxiosRequestConfig) => {
      /*
       * Capture original request ID as correlation ID on first retry
       * correlationId is a dynamic property added at runtime for tracking retry chains
       */
      const configWithCorrelation = requestConfig as AxiosRequestConfig & { correlationId?: string };
      if (retryCount === 1 && !configWithCorrelation.correlationId) {
        configWithCorrelation.correlationId = error.config?.headers?.[HeaderRequestId.NAME];
      }
      requestConfig.headers = Object.assign({}, requestConfig.headers || {}, new HeaderRequestId().toHeader());
    },
    onMaxRetryTimesExceeded: (error: AxiosError, retryCount: number) => {
      if (error.config?.url && !isStatsUrl(error.config.url)) {
        const sanitizedError = omit(error, OMIT_FROM_ERROR_LOGGING);
        const requestId: string = getRequestIdFromAxiosError(error);
        Logger.trackEvent({
          action: LogAction.API_CALL_FAILED_AFTER_RETRY,
          elementInfo: {
            eventType: EventType.EVENT_ERROR,
            metadata: JSON.stringify({
              numberOfRetries: retryCount,
              requestUrl: getRequestUrlFromConfig(error.config),
              requestMethod: error.config?.method,
            }),
          },
          error: { requestId, errorStackTrace: JSON.stringify(sanitizedError) },
        });
      }
    },
  });
};

export const setupAxiosResponseIntercept = (
  axiosApiInstance: AxiosInstance,
  optionalPayloadConsumer?: (payload: Pick<GenStatV2, 'attrib1' | 'attrib2' | 'data1'>) => void,
  optionalResponseInterceptor?: (response: AxiosResponse) => AxiosResponse,
  reloadNotification?: () => void
) => {
  axiosApiInstance.interceptors.request.use(axiosCheckSensitiveDataInterceptor);
  axiosApiInstance.interceptors.response.use(
    (response: AxiosResponse<unknown>) =>
      interceptFulfilledResponse(response, optionalResponseInterceptor, reloadNotification),
    (error: AxiosError) => handleApiFailure(error, optionalPayloadConsumer, reloadNotification)
  );
  setupRetry(axiosApiInstance);
};
